import React from 'react';
import {Flex, Text} from '@priority-software/priority-style-react';
import styles from '../styles/fileCard.module.scss';
import classnames from 'classnames';
import {Record} from '../../../../../../stores/Form/Record';
import {getColumnTitle, getFieldValueByName} from '../../Common/utils';
import {getImgByExt} from '../utils';
import texts from '../../texts';
import {FileFields, FileStatus} from '../consts';
import {EditDropdown} from './EditDropdown';
import {getFileData} from '../actions';
import {previewFile} from '../../../../utils/file-processing';
import {highlightText} from '../../../../../../_legacy/library/utils/highlightText';
import {MutableRefObject} from 'react';
import {DetailsContent} from '../../Common/components/DetailsContent';
import {Tooltip} from '../../../../../../components/Common/Tooltip/index';
import {createDataTestId} from '../../../../../../utils/dataTestId';

interface FileCardProps {
    file: Record;
    searchQuery: string;
    containerRef: MutableRefObject<HTMLDivElement>;
    enabledCols: string[];
    setIsDownloading: (boolean) => void;
    setParentNeedsRefresh: (value: boolean) => void;
}

export const FileCard = ({
    file,
    searchQuery,
    containerRef,
    enabledCols,
    setIsDownloading,
    setParentNeedsRefresh
}: FileCardProps) => {
    const fileSize = getFieldValueByName(file, FileFields.fileSize);
    const isReadOnly = getFieldValueByName(file, FileFields.status) === FileStatus.readOnly;
    const isNoSend = getFieldValueByName(file, FileFields.noSend);
    const isDataExchange = getFieldValueByName(file, FileFields.dataExchange);
    const extension = getFieldValueByName(file, FileFields.extension).toString();

    const loadFileData = async file => {
        setIsDownloading(true);
        const data = await getFileData(file);
        setIsDownloading(false);
        return data;
    };
    const onPreviewFile = async () => {
        const fileData = await loadFileData(file);
        const fileName = getFieldValueByName(file, FileFields.fileName);
        previewFile(fileData, fileName.toString());
    };

    return (
        <>
            <Flex
                componentClasses={classnames(styles.card, {
                    [styles.newCard]: file.isRecent,
                    [styles.openDetails]: !!enabledCols.length
                })}
                {...createDataTestId(`attachments-fileCard`, true)}
            >
                <Flex justifyContent={'flex-start'}>
                    <div className={styles.iconsWrapper}>
                        <img
                            src={getImgByExt(extension)}
                            alt={extension}
                            draggable={false}
                            width="40"
                            height="40"
                        />
                    </div>
                    <Flex direction={'column'} alignItems={'flex-start'}>
                        <div onClick={onPreviewFile} className={styles.fileName}>
                            <Text skin={'paragraph-1'} componentClasses={styles.fileName}>
                                {highlightText(
                                    `${getFieldValueByName(
                                        file,
                                        FileFields.fileName
                                    )}.${extension}`,
                                    searchQuery
                                )}
                            </Text>
                        </div>
                        <Flex justifyContent="flex-start">
                            {fileSize && (
                                <>
                                    <Text
                                        skin={'label-1'}
                                        textColor="secondary-500"
                                        {...createDataTestId(
                                            `attachments-fileCard-file-size`,
                                            true
                                        )}
                                    >
                                        {highlightText(`${fileSize}B`, searchQuery)}
                                    </Text>
                                    <div className={styles.textSeparator} />
                                </>
                            )}
                            <Text
                                skin={'label-1'}
                                textColor="secondary-500"
                                {...createDataTestId(`attachments-fileCard-userName`, true)}
                            >
                                {highlightText(
                                    `${getFieldValueByName(file, FileFields.userName)}`,
                                    searchQuery
                                )}
                            </Text>
                            <div className={styles.textSeparator} />
                            <Text
                                skin={'label-1'}
                                textColor="secondary-500"
                                {...createDataTestId(`attachments-fileCard-date`, true)}
                            >
                                {highlightText(
                                    `${getFieldValueByName(file, FileFields.fileDate)}`,
                                    searchQuery
                                )}
                            </Text>
                        </Flex>
                    </Flex>
                </Flex>
                <Flex direction={'column'}>
                    {isNoSend && (
                        <Text
                            skin="tiny"
                            textColor="validation-red"
                            componentClasses={classnames(styles.label, styles.dontSend)}
                            {...createDataTestId(`attachments-badge-dontSend`, true)}
                        >
                            {texts.label_not_for_sending}
                        </Text>
                    )}
                    {isReadOnly && (
                        <Tooltip
                            tooltip={`${getColumnTitle(
                                file,
                                FileFields.lockUser
                            )} ${getFieldValueByName(file, FileFields.lockUser)}`}
                            zIndex={10006}
                            position={'top center'}
                            {...createDataTestId(`attachments-tooltip-readOnly`, true)}
                        >
                            <Text
                                skin="tiny"
                                textColor="primary-700"
                                componentClasses={classnames(styles.label, styles.readonly)}
                                {...createDataTestId(`attachments-badge-readOnly`, true)}
                            >
                                {texts.label_readonly}
                            </Text>
                        </Tooltip>
                    )}
                </Flex>
                <EditDropdown
                    isReadOnly={isReadOnly}
                    isNoSend={!!isNoSend}
                    isDataExchange={!!isDataExchange}
                    file={file}
                    containerRef={containerRef}
                    loadFileData={loadFileData}
                    setParentNeedsRefresh={setParentNeedsRefresh}
                />
            </Flex>
            <DetailsContent
                enabledCols={enabledCols}
                record={file}
                componentClasses={styles.detailsWrapper}
            />
        </>
    );
};
